import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FiCalendar, FiSearch, FiDownload, FiUser, FiClock,
  FiCheckCircle, FiXCircle, FiAlertCircle, FiPieChart,
  FiChevronLeft, FiChevronRight, FiRefreshCw, FiBarChart2,
  FiFilter, FiCamera, FiUpload, FiTrash2, FiUsers, FiEye,
  FiMapPin, FiNavigation, FiX, FiPlus, FiImage, FiInfo
} from 'react-icons/fi';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useAppContext } from '../context/AppContext';
import attendanceService from '../services/attendanceService';
import { getCurrentUser } from '../services/authService';

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Attendance = () => {
  const { employees: globalEmployees, refreshData } = useAppContext();
  const currentUser = getCurrentUser();
  const isAdminOrHR = useMemo(() => attendanceService.isAdminOrHR(), []);
  const currentEmployeeId = useMemo(() => attendanceService.getCurrentEmployeeId(), []);
  const [loadingEmployeeStats, setLoadingEmployeeStats] = useState(false);

  // State for attendance data
  const [rawAttendanceData, setRawAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [tableFilteredData, setTableFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedTableDate, setSelectedTableDate] = useState(getLocalDateString());

  // Employee detail modal
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [employeeAttendanceRecords, setEmployeeAttendanceRecords] = useState([]);

  // Face verification states
  const [showCamera, setShowCamera] = useState(false);
  const [captureMode, setCaptureMode] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showFaceUpload, setShowFaceUpload] = useState(false);
  const [selectedEmployeeForFace, setSelectedEmployeeForFace] = useState(null);
  const [faceImages, setFaceImages] = useState([]);
  const [uploadingFace, setUploadingFace] = useState(false);
  const [existingFaceImages, setExistingFaceImages] = useState([]);
  const [loadingFaceImages, setLoadingFaceImages] = useState(false);

  // Location states
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null);

  // Permission request states
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [permissionStep, setPermissionStep] = useState('');

  // Date states
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const [dateRange, setDateRange] = useState({
    start: getLocalDateString(new Date(currentYear, currentMonth, 1)),
    end: getLocalDateString(new Date(currentYear, currentMonth + 1, 0))
  });

  // Statistics state
  const [statistics, setStatistics] = useState({
    presentDays: 0,
    absent: 0,
    leaves: 0,
    lateMarks: 0,
    totalDays: 0,
    attendancePercentage: 0,
    totalHoursWorked: 0,
    averageHoursPerDay: 0,
    totalLateMinutes: 0,
    totalWorkingDays: 0,
    totalOvertimeHours: 0
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  // ============ HELPER FUNCTIONS ============

  const sameEmployeeId = useCallback((a, b) => {
    if (a === null || a === undefined || b === null || b === undefined) return false;
    return String(a) === String(b);
  }, []);

  const imageBelongsToEmployee = useCallback((img, employeeId) => {
    if (!img) return false;
    const empId = String(employeeId);
    const employeeField = img.employee;
    const employeeFieldId = employeeField && typeof employeeField === 'object'
      ? employeeField.id ?? employeeField.employee_id
      : employeeField;
    return (
      (employeeFieldId !== undefined && employeeFieldId !== null && String(employeeFieldId) === empId) ||
      (img.employee_id !== undefined && img.employee_id !== null && String(img.employee_id) === empId)
    );
  }, []);

  const enrichRecordsWithEmployeeInfo = useCallback((records) => {
    if (!Array.isArray(records) || records.length === 0 || !Array.isArray(globalEmployees)) {
      return records;
    }
    return records.map(record => {
      const match = globalEmployees.find(emp =>
        sameEmployeeId(emp.id, record.employeeId) ||
        sameEmployeeId(emp.employee_id, record.employeeId)
      );
      if (!match) return record;
      return {
        ...record,
        employeeId: match.employee_id || match.id,
        employeeName: (!record.employeeName || record.employeeName === 'Unknown')
          ? (match.name || match.full_name || record.employeeName)
          : record.employeeName,
        department: (!record.department || record.department === 'General')
          ? (match.department || record.department)
          : record.department,
      };
    });
  }, [globalEmployees, sameEmployeeId]);

  const attendanceData = useMemo(
    () => enrichRecordsWithEmployeeInfo(rawAttendanceData),
    [rawAttendanceData, enrichRecordsWithEmployeeInfo]
  );

  const isMobileDevice = useCallback(() => {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsVideoReady(false);
  }, []);

  // ============ LOCATION FUNCTIONS ============

  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      setIsGettingLocation(true);
      setLocationError(null);

      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser');
        setIsGettingLocation(false);
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };
          setLocation(loc);
          setLocationError(null);
          setIsGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          let errorMsg = 'Unable to get location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location permission denied. Please allow location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location information is unavailable. Please check your GPS signal.';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out. Please try again.';
              break;
            default:
              errorMsg += error.message || 'Unknown error occurred.';
          }
          setLocationError(errorMsg);
          setIsGettingLocation(false);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0
        }
      );
    });
  }, []);

  // ============ LOAD DATA FUNCTION ============

  const loadAttendanceData = useCallback(async () => {
    setLoading(true);
    try {
      let backendData = [];
      if (!isAdminOrHR && currentEmployeeId) {
        try {
          backendData = await attendanceService.getMyAttendance({
            start_date: dateRange.start,
            end_date: dateRange.end
          });
        } catch (error) {
          ('Error fetching employee attendance:', error);
          backendData = [];
        }
      } else {
        try {
          backendData = await attendanceService.getAll({
            start_date: dateRange.start,
            end_date: dateRange.end
          });
        } catch (error) {
          ('Error fetching all attendance:', error);
          backendData = [];
        }
      }
      const data = attendanceService.transformRecords(backendData);
      setRawAttendanceData(data);
    } catch (error) {
      ('Error loading attendance:', error);
      setRawAttendanceData([]);
    } finally {
      setLoading(false);
    }
  }, [isAdminOrHR, currentEmployeeId, dateRange]);

  // ============ FACE VERIFICATION FUNCTIONS ============

  const verifyFace = useCallback(async (imageData) => {
    setIsVerifying(true);
    setVerificationStatus('verifying');
    try {
      const response = await fetch(imageData);
      const blob = await response.blob();
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(blob);
      const base64String = await base64Promise;
      const formData = new FormData();
      formData.append('employee_id', currentEmployeeId || '');
      formData.append('attendance_type', captureMode === 'check_in' ? 'check_in' : 'check_out');
      formData.append('photo_base64', base64String);
      if (location) {
        const lat = parseFloat(location.latitude.toFixed(6));
        const lng = parseFloat(location.longitude.toFixed(6));
        formData.append('latitude', lat);
        formData.append('longitude', lng);
        if (location.accuracy) {
          formData.append('accuracy', parseFloat(location.accuracy.toFixed(0)));
        }
      }
      const result = await attendanceService.verifyFace(formData);
      const isFailureMessage = result?.message && /fail|error|denied|not\s*match/i.test(result.message);
      const verified = Boolean(
        !isFailureMessage && (
          result?.success === true ||
          result?.is_verified === true ||
          (result?.message && /success/i.test(result.message))
        )
      );
      if (verified) {
        setVerificationStatus('success');
        await loadAttendanceData();
        const locationInfo = location
          ? `\n Location: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`
          : '';
        const successMessage = result?.message ||
          (captureMode === 'check_in' ? 'Check-in successful!' : 'Check-out successful!');
        alert(`✅ ${successMessage}${locationInfo}`);
        setShowCamera(false);
        setCapturedImage(null);
        setCaptureMode(null);
        setLocation(null);
        stopCamera();
      } else {
        setVerificationStatus('failed');
        alert(result?.message || 'Face verification failed. Please try again.');
      }
    } catch (error) {
      ('Face verification error:', error);
      setVerificationStatus('error');
      alert('Error during face verification. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }, [currentEmployeeId, captureMode, location, loadAttendanceData, stopCamera]);

  // ============ CAPTURE PHOTO FUNCTION ============

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      alert('Camera or canvas not available. Please try again.');
      return;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (video.readyState < 2) {
        alert('Camera is not ready. Please wait a moment and try again.');
        return;
      }

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        alert('Camera is not initialized. Please try again.');
        return;
      }

      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      canvas.width = videoWidth;
      canvas.height = videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL('image/jpeg', 0.95);

      if (imageData === 'data:image/jpeg;base64,' || imageData.length < 1000) {
        alert('Failed to capture image. Please ensure camera is working and try again.');
        return;
      }

      setCapturedImage(imageData);
      alert('Photo captured successfully! Click "Verify Face" to proceed.');

    } catch (error) {
      ('Capture error:', error);
      alert('Failed to capture image: ' + error.message);
    }
  }, []);

  // ============ CONFIRM LOCATION AND PROCEED ============

  const confirmLocationAndProceed = useCallback(async () => {
    setShowLocationModal(false);
    setIsRequestingPermission(true);
    setPermissionStep('camera');
    setIsVideoReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      });
      streamRef.current = stream;
      setShowCamera(true);
      setVerificationStatus(null);
      setCapturedImage(null);
      setIsRequestingPermission(false);

    } catch (error) {
      ('Camera error:', error);
      setIsRequestingPermission(false);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert(' Camera access is required for face verification.\n\nPlease allow camera access.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert('No camera found on your device. Please use a device with a camera.');
      } else {
        alert('Unable to access camera. Please check camera permissions.');
      }
      setShowCamera(false);
    }
  }, []);

  useEffect(() => {
    if (!showCamera || !streamRef.current || !videoRef.current) return;

    const videoEl = videoRef.current;
    videoEl.srcObject = streamRef.current;

    const markReady = () => {
      videoEl.play()
        .then(() => {
          setIsVideoReady(true);
        })
        .catch(err => {
          ('Play error:', err);
          setTimeout(() => {
            videoEl.play()
              .then(() => setIsVideoReady(true))
              .catch(e => ('Retry play failed:', e));
          }, 500);
        });
    };

    videoEl.onloadedmetadata = markReady;

    const fallbackTimer = setTimeout(() => {
      if (videoEl.readyState >= 2) {
        markReady();
      }
    }, 1000);

    return () => {
      clearTimeout(fallbackTimer);
      videoEl.onloadedmetadata = null;
    };
  }, [showCamera]);

  const startFaceVerification = useCallback(async (mode) => {
    setCaptureMode(mode);
    setIsRequestingPermission(true);
    setPermissionStep('location');

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 30000,
          maximumAge: 0
        });
      });

      const loc = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp
      };

      setLocation(loc);
      setLocationError(null);

      setPermissionStep('camera');

      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });

        testStream.getTracks().forEach(track => track.stop());

        setShowLocationModal(true);
        setIsRequestingPermission(false);

      } catch (cameraError) {
        ('Camera error:', cameraError);
        setIsRequestingPermission(false);

        if (cameraError.name === 'NotAllowedError' || cameraError.name === 'PermissionDeniedError') {
          alert('Camera access is required for face verification.\n\nPlease allow camera access ');
        } else {
          alert('Unable to access camera. Please check camera permissions.');
        }
      }

    } catch (locationError) {
      ('Location error:', locationError);
      setIsRequestingPermission(false);

      if (locationError.code === 1) {
        alert('Location access is required for attendance.\n\nPlease allow location access.');
      } else if (locationError.code === 3) {
        alert('Location request timed out. Please ensure GPS is enabled and try again.');
        if (window.confirm('Would you like to try again?')) {
          startFaceVerification(mode);
        }
      } else {
        alert('Unable to get location. Please ensure GPS is enabled.');
        if (window.confirm('Would you like to try again?')) {
          startFaceVerification(mode);
        }
      }
    }
  }, []);

  // ============ FACE IMAGE MANAGEMENT (HR) ============

  const loadExistingFaceImages = useCallback(async (employeeId) => {
    if (!employeeId) return [];
    setLoadingFaceImages(true);
    try {
      const images = await attendanceService.getFaceImages(employeeId);
      const safeImages = Array.isArray(images) ? images : [];
      setExistingFaceImages(safeImages);
      return safeImages;
    } catch (error) {
      ('Error loading face images:', error);
      setExistingFaceImages([]);
      return [];
    } finally {
      setLoadingFaceImages(false);
    }
  }, []);

  const deleteFaceImage = useCallback(async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this face image?')) return;

    try {
      await attendanceService.deleteFaceImage(imageId);
      alert('Face image deleted successfully!');
      if (selectedEmployeeForFace) {
        await loadExistingFaceImages(selectedEmployeeForFace);
      }
    } catch (error) {
      ('Error deleting face image:', error);
      alert('Failed to delete face image.');
    }
  }, [selectedEmployeeForFace, loadExistingFaceImages]);

  const uploadFaceImages = useCallback(async () => {
    if (!selectedEmployeeForFace) {
      alert('Please select an employee first.');
      return;
    }

    if (faceImages.length === 0) {
      alert('Please select at least 1 image to upload.');
      return;
    }

    if (faceImages.length > 4) {
      alert('Maximum 4 images allowed.');
      return;
    }

    setUploadingFace(true);

    try {
      const latestFaceImages = await loadExistingFaceImages(selectedEmployeeForFace);

      const existingPhotoNumbers = latestFaceImages.map(img => img.photo_number || 0);
      const allPhotoNumbers = [1, 2, 3, 4];
      const availablePhotoNumbers = allPhotoNumbers.filter(num => !existingPhotoNumbers.includes(num));

      if (availablePhotoNumbers.length < faceImages.length) {
        alert(` This employee already has ${existingPhotoNumbers.length} photo(s). Only ${availablePhotoNumbers.length} slot(s) available. Please delete existing photos first.`);
        setUploadingFace(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      const uploadedPhotos = [];

      for (let i = 0; i < faceImages.length; i++) {
        const photoNumber = availablePhotoNumbers[i];

        const formData = new FormData();
        formData.append('employee', selectedEmployeeForFace);
        formData.append('photo', faceImages[i]);
        formData.append('photo_number', photoNumber);

        try {
          const response = await attendanceService.uploadSingleFaceImage(formData);

          if (response && (response.id || response.success)) {
            successCount++;
            uploadedPhotos.push(photoNumber);
          } else {
            errorCount++;
            const errorMsg = response?.message || 'Unknown error';
            errors.push(`Photo #${photoNumber}: ${errorMsg}`);
          }
        } catch (err) {
          errorCount++;
          let errorMsg = 'Upload failed';
          if (err.response?.data) {
            const data = err.response.data;
            if (typeof data === 'object') {
              const errorFields = Object.keys(data).map(key => `${key}: ${data[key]}`).join(', ');
              errorMsg = errorFields || 'Validation error';
            } else {
              errorMsg = data || err.message;
            }
          } else {
            errorMsg = err.message || 'Network error';
          }
          errors.push(`Photo #${photoNumber}: ${errorMsg}`);
          (`❌ Error uploading photo ${photoNumber}:`, err);
        }
      }

      if (successCount === faceImages.length) {
        alert(` All ${successCount} face image(s) uploaded successfully!\n\n📸 Photo numbers: ${uploadedPhotos.join(', ')}`);
        setFaceImages([]);
        await loadExistingFaceImages(selectedEmployeeForFace);
      } else if (successCount > 0) {
        alert(` ${successCount} of ${faceImages.length} photo(s) uploaded successfully.\n\n✅ Uploaded: ${uploadedPhotos.join(', ')}\n❌ Failed: ${errors.join('\n')}`);
        setFaceImages([]);
        await loadExistingFaceImages(selectedEmployeeForFace);
      } else {
        alert(` Failed to upload any photos.\n\n${errors.join('\n')}`);
      }

    } catch (error) {
      ('Error uploading face images:', error);
      alert(`Failed to upload face images: ${error.message || 'Unknown error'}`);
    } finally {
      setUploadingFace(false);
    }
  }, [selectedEmployeeForFace, faceImages, loadExistingFaceImages]);

  // ============ FILTERS AND CALCULATIONS ============

  const calculateStatistics = useCallback((data) => {
    const realData = data.filter(d => !d.isPlaceholder);
    const present = realData.filter(d => d.status === 'Present').length;
    const absent = realData.filter(d => d.status === 'Absent').length;
    const leaves = realData.filter(d => d.status === 'Leave').length;
    const late = realData.filter(d => d.status === 'Late').length;
    const total = present + absent + leaves + late;

    let totalHours = 0;
    let lateMinutes = 0;
    let overtimeHours = 0;

    realData.forEach(record => {
      if (record.totalHours && record.totalHours !== '0') {
        const hours = parseFloat(record.totalHours);
        totalHours += hours;
        if (hours > 8) {
          overtimeHours += (hours - 8);
        }
      }
      if (record.status === 'Late') {
        if (record.checkIn) {
          const [hours, minutes] = record.checkIn.split(':').map(Number);
          const lateTime = (hours * 60 + minutes) - (9 * 60 + 30);
          if (lateTime > 0) {
            lateMinutes += lateTime;
          }
        }
      }
    });

    const workingDays = present + late;

    setStatistics({
      presentDays: present,
      absent: absent,
      leaves: leaves,
      lateMarks: late,
      totalDays: total,
      attendancePercentage: total > 0 ? (((present + late) / total) * 100).toFixed(1) : 0,
      totalHoursWorked: totalHours,
      averageHoursPerDay: total > 0 ? (totalHours / total) : 0,
      totalLateMinutes: lateMinutes,
      totalWorkingDays: workingDays,
      totalOvertimeHours: overtimeHours
    });
  }, []);

  const applyMainFilters = useCallback(() => {
    let filtered = [...attendanceData].filter(d => !d.isPlaceholder);
    filtered = filtered.filter(r => r.date >= dateRange.start && r.date <= dateRange.end);

    if (isAdminOrHR && selectedEmployee !== 'all') {
      filtered = filtered.filter(r => sameEmployeeId(r.employeeId, selectedEmployee));
    } else if (!isAdminOrHR && currentEmployeeId) {
      filtered = filtered.filter(r => sameEmployeeId(r.employeeId, currentEmployeeId));
    }

    if (isAdminOrHR && selectedDepartment !== 'all') {
      filtered = filtered.filter(r => r.department === selectedDepartment);
    }

    if (isAdminOrHR && searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.employeeName.toLowerCase().includes(term) ||
        String(r.employeeId).toLowerCase().includes(term)
      );
    }

    setFilteredData(filtered);
    calculateStatistics(filtered);
  }, [attendanceData, dateRange, selectedEmployee, selectedDepartment, searchTerm, calculateStatistics, isAdminOrHR, currentEmployeeId, sameEmployeeId]);

  const generateEmptyTodayRecords = useCallback((employees, date) => {
    const today = getLocalDateString();
    if (date !== today) return [];
    return employees.map(emp => ({
      id: `PLACEHOLDER_${emp.employee_id || emp.id}_${date}`,
      employeeId: emp.employee_id || emp.id,
      employeeName: emp.name || emp.full_name || 'Employee',
      department: emp.department || 'General',
      date: date,
      checkIn: '',
      checkOut: '',
      totalHours: '0',
      status: 'Not Marked',
      overtime: 0,
      isPlaceholder: true,
      latitude: null,
      longitude: null
    }));
  }, []);

  const applyTableFilters = useCallback(() => {
    const today = getLocalDateString();
    let realRecords = attendanceData.filter(r => r.date === selectedTableDate && !r.isPlaceholder);

    if (!isAdminOrHR && currentEmployeeId) {
      realRecords = realRecords.filter(r => sameEmployeeId(r.employeeId, currentEmployeeId));
    }

    if (isAdminOrHR && searchTerm) {
      const term = searchTerm.toLowerCase();
      realRecords = realRecords.filter(r =>
        r.employeeName.toLowerCase().includes(term) ||
        String(r.employeeId).toLowerCase().includes(term)
      );
    }

    if (isAdminOrHR && selectedEmployee !== 'all') {
      realRecords = realRecords.filter(r => sameEmployeeId(r.employeeId, selectedEmployee));
    }

    if (isAdminOrHR && selectedDepartment !== 'all') {
      realRecords = realRecords.filter(r => r.department === selectedDepartment);
    }

    if (selectedTableDate === today && isAdminOrHR) {
      const existingEmployeeIds = new Set(realRecords.map(r => String(r.employeeId)));
      let employeesToShow = globalEmployees.filter(e =>
        !existingEmployeeIds.has(String(e.employee_id || e.id))
      );

      if (selectedEmployee !== 'all') {
        employeesToShow = employeesToShow.filter(e =>
          sameEmployeeId(e.employee_id || e.id, selectedEmployee)
        );
      }

      if (selectedDepartment !== 'all') {
        employeesToShow = employeesToShow.filter(e => e.department === selectedDepartment);
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        employeesToShow = employeesToShow.filter(e =>
          (e.name || '').toLowerCase().includes(term) ||
          String(e.employee_id || e.id).toLowerCase().includes(term)
        );
      }

      const emptySlots = generateEmptyTodayRecords(employeesToShow, today);
      realRecords = [...realRecords, ...emptySlots];
    }

    setTableFilteredData(realRecords);
    setCurrentPage(1);
  }, [attendanceData, selectedTableDate, searchTerm, selectedEmployee, selectedDepartment, isAdminOrHR, currentEmployeeId, globalEmployees, generateEmptyTodayRecords, sameEmployeeId]);

  // ============ EMPLOYEE DETAIL FUNCTIONS ============

  const getNumericEmployeeId = useCallback((employeeId) => {
    if (!employeeId) return null;

    if (typeof employeeId === 'number') return employeeId;

    if (/^\d+$/.test(String(employeeId))) {
      return parseInt(employeeId, 10);
    }

    if (Array.isArray(globalEmployees)) {
      const employee = globalEmployees.find(emp =>
        emp.employee_id === employeeId ||
        String(emp.id) === String(employeeId)
      );
      if (employee) {
        return employee.id;
      }
    }

    return null;
  }, [globalEmployees]);

  const fetchEmployeeStats = useCallback(async (employeeId) => {
    if (!employeeId) {
      setEmployeeStats(null);
      setEmployeeAttendanceRecords([]);
      return;
    }

    const numericId = getNumericEmployeeId(employeeId);

    if (!numericId) {
      ('❌ Could not find numeric ID for employee:', employeeId);
      alert('Employee not found. Please refresh and try again.');
      setEmployeeStats(null);
      setEmployeeAttendanceRecords([]);
      return;
    }

    setLoadingEmployeeStats(true);

    try {

      const response = await attendanceService.getEmployeeAttendanceData(
        numericId,
        {
          year: currentYear,
          month: currentMonth + 1
        }
      );


      if (response) {
        if (response.monthly_summary) {
          setEmployeeStats(response.monthly_summary);
        } else if (response.statistics) {
          setEmployeeStats(response.statistics);
        } else {
          setEmployeeStats(null);
        }

        if (response.recent_records && response.recent_records.length > 0) {
          const transformedRecords = response.recent_records.map(record => ({
            id: record.id,
            date: record.date,
            checkIn: record.check_in || record.checkIn || '—',
            checkOut: record.check_out || record.checkOut || '—',
            totalHours: record.total_hours || record.totalHours || '0',
            status: record.status || 'Absent',
            late_minutes: record.late_minutes || record.lateMinutes || '0',
            overtime: record.overtime || '0',
          }));
          setEmployeeAttendanceRecords(transformedRecords);
        } else {
          setEmployeeAttendanceRecords([]);
        }

        if (response.employee) {
          setSelectedEmployeeDetail(prev => ({
            ...prev,
            id: numericId,
            name: response.employee.name || prev?.name || 'Unknown',
            employeeId: response.employee.employee_id || employeeId,
            department: response.employee.department || 'General',
            email: response.employee.email || '',
            phone: response.employee.phone || '',
            designation: response.employee.designation || '',
            joining_date: response.employee.joining_date || '',
          }));
        }


      }

    } catch (error) {
      ('Error fetching employee stats:', error);
      setEmployeeStats(null);
      setEmployeeAttendanceRecords([]);
      alert('Failed to load employee attendance data. Please try again.');
    } finally {
      setLoadingEmployeeStats(false);
    }
  }, [currentYear, currentMonth, getNumericEmployeeId]);

  const handleEmployeeClick = useCallback(async (employeeId, employeeName) => {
    if (!employeeId) {
      ('⚠️ No employee ID provided');
      return;
    }


    setShowEmployeeDetail(true);
    setLoadingEmployeeStats(true);

    setSelectedEmployeeDetail({
      id: employeeId,
      name: employeeName || 'Unknown Employee'
    });

    await fetchEmployeeStats(employeeId);
  }, [fetchEmployeeStats]);

  // ============ HANDLERS ============

  const handleRefresh = useCallback(async () => {
    await loadAttendanceData();
    if (refreshData) refreshData();
  }, [loadAttendanceData, refreshData]);

  const exportToCSV = useCallback(async () => {
    try {
      const params = {
        start_date: dateRange.start,
        end_date: dateRange.end,
        employee: selectedEmployee !== 'all' ? selectedEmployee : undefined,
        department: selectedDepartment !== 'all' ? selectedDepartment : undefined
      };
      await attendanceService.exportCSV(params);
    } catch (error) {
      const realData = tableFilteredData.filter(r => !r.isPlaceholder);
      const headers = ['Date', 'Employee ID', 'Employee Name', 'Department', 'Check In', 'Check Out', 'Total Hours', 'Status', 'Latitude', 'Longitude'];
      const csvData = realData.map(r => [
        r.date, r.employeeId, r.employeeName, r.department,
        r.checkIn || '-', r.checkOut || '-', r.totalHours, r.status,
        r.latitude || '', r.longitude || ''
      ]);
      const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${selectedTableDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }, [tableFilteredData, selectedTableDate, dateRange, selectedEmployee, selectedDepartment]);

  // ============ EFFECTS ============

  useEffect(() => {
    loadAttendanceData();
  }, [loadAttendanceData]);

  useEffect(() => {
    applyMainFilters();
  }, [applyMainFilters]);

  useEffect(() => {
    applyTableFilters();
  }, [applyTableFilters]);

  useEffect(() => {
    if (selectedEmployeeForFace && showFaceUpload) {
      loadExistingFaceImages(selectedEmployeeForFace);
    }
  }, [selectedEmployeeForFace, showFaceUpload, loadExistingFaceImages]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // ============ MEMOIZED VALUES ============

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(() =>
    tableFilteredData.slice(indexOfFirstItem, indexOfLastItem),
    [tableFilteredData, indexOfFirstItem, indexOfLastItem]
  );
  const totalPages = Math.ceil(tableFilteredData.length / itemsPerPage);
  const paginate = useCallback((p) => setCurrentPage(p), []);

  useEffect(() => {
    if (currentPage > 1 && currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [totalPages, currentPage]);

  const pieChartData = useMemo(() => {
    const real = filteredData.filter(d => !d.isPlaceholder);
    const present = real.filter(d => d.status === 'Present').length;
    const absent = real.filter(d => d.status === 'Absent').length;
    const leaves = real.filter(d => d.status === 'Leave').length;
    const late = real.filter(d => d.status === 'Late').length;
    const data = [];
    if (present > 0) data.push({ name: 'Present', value: present, color: '#10B981' });
    if (absent > 0) data.push({ name: 'Absent', value: absent, color: '#EF4444' });
    if (leaves > 0) data.push({ name: 'Leave', value: leaves, color: '#F59E0B' });
    if (late > 0) data.push({ name: 'Late', value: late, color: '#8B5CF6' });
    if (data.length === 0) data.push({ name: 'No Data', value: 1, color: '#E5E7EB' });
    return data;
  }, [filteredData]);

  const departments = useMemo(() =>
    [...new Set(globalEmployees.map(emp => emp.department || 'General'))],
    [globalEmployees]
  );

  const getStatusBadge = useCallback((status, isPlaceholder) => {
    if (isPlaceholder) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
          <FiClock className="mr-1" size={12} />Not marked
        </span>
      );
    }
    switch (status) {
      case 'Present': return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700"><FiCheckCircle className="mr-1" size={12} />Present</span>;
      case 'Absent': return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700"><FiXCircle className="mr-1" size={12} />Absent</span>;
      case 'Leave': return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700"><FiCalendar className="mr-1" size={12} />Leave</span>;
      case 'Late': return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700"><FiAlertCircle className="mr-1" size={12} />Late</span>;
      default: return <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  }, []);

  // ============ RENDER ============

  if (loading && attendanceData.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-full px-2 sm:px-4 md:px-6 pb-6">
      {/* Page Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="w-full sm:w-auto">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Attendance Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              {isAdminOrHR ? 'Track and manage employee attendance' : 'View your attendance records'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {!isAdminOrHR && (
              <>
                <button
                  onClick={() => startFaceVerification('check_in')}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-sm flex items-center justify-center gap-2 min-w-[100px]"
                  disabled={isVerifying || isRequestingPermission}
                >
                  {isRequestingPermission && permissionStep === 'location' ? (
                    <span className="flex items-center gap-1">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      <span className="hidden xs:inline">Loading...</span>
                    </span>
                  ) : (
                    <>
                      <FiCamera size={16} />
                      <span className="hidden xs:inline">Check In</span>
                      <span className="xs:hidden">In</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => startFaceVerification('check_out')}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition text-sm flex items-center justify-center gap-2 min-w-[100px]"
                  disabled={isVerifying || isRequestingPermission}
                >
                  {isRequestingPermission && permissionStep === 'location' ? (
                    <span className="flex items-center gap-1">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      <span className="hidden xs:inline">Loading...</span>
                    </span>
                  ) : (
                    <>
                      <FiCamera size={16} />
                      <span className="hidden xs:inline">Check Out</span>
                      <span className="xs:hidden">Out</span>
                    </>
                  )}
                </button>
              </>
            )}
            {isAdminOrHR && (
              <button
                onClick={() => setShowFaceUpload(true)}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm flex items-center justify-center gap-2"
              >
                <FiUpload size={16} />
                <span className="hidden xs:inline">Upload Face Images</span>
                <span className="xs:hidden">Upload</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Permission Request Modal */}
      {isRequestingPermission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
            {permissionStep === 'location' && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiMapPin className="text-blue-600 text-3xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Accessing Location</h3>
                <p className="text-sm text-gray-500 mt-2">Please allow location access when prompted by your browser.</p>
              </>
            )}
            {permissionStep === 'camera' && (
              <>
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCamera className="text-purple-600 text-3xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Accessing Camera</h3>
                <p className="text-sm text-gray-500 mt-2">Please allow camera access when prompted by your browser.</p>
              </>
            )}
            <p className="text-xs text-gray-400 mt-4">
              {isMobileDevice() ? '📱 Your browser will show a permission request' : '🖥️ Your browser will show a permission request'}
            </p>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FiNavigation className="text-blue-600 text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Location Detected</h3>
              <p className="text-sm text-gray-500 mt-1">Your location has been captured for attendance verification.</p>
            </div>
            {location && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Latitude:</span><span className="ml-2 font-medium text-gray-800">{location.latitude.toFixed(6)}</span></div>
                  <div><span className="text-gray-500">Longitude:</span><span className="ml-2 font-medium text-gray-800">{location.longitude.toFixed(6)}</span></div>
                  <div className="col-span-2"><span className="text-gray-500">Accuracy:</span><span className="ml-2 font-medium text-gray-800">{location.accuracy.toFixed(1)} meters</span></div>
                </div>
              </div>
            )}
            {locationError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                <p className="text-red-600 text-sm flex items-center gap-2"><FiAlertCircle size={16} />{locationError}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setShowLocationModal(false); setLocation(null); }} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">Cancel</button>
              <button onClick={confirmLocationAndProceed} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2"><FiCamera size={16} />Proceed to Capture</button>
            </div>
          </div>
        </div>
      )}

      {/* Face Verification Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-4 sm:p-6 relative">
            <button onClick={() => { setShowCamera(false); setCapturedImage(null); setCaptureMode(null); setVerificationStatus(null); stopCamera(); }} className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 p-2 z-10"><FiX size={24} /></button>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 pr-8">Face Verification - {captureMode === 'check_in' ? 'Check In' : 'Check Out'}</h3>
            <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video max-h-[400px]">
              {!capturedImage ? (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              ) : (
                <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" />
              )}
              <canvas ref={canvasRef} className="hidden" />
              {!capturedImage && !isVideoReady && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3"></div><p className="text-sm">Loading camera...</p></div>
                </div>
              )}
              {verificationStatus === 'verifying' && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-3"></div><p className="text-sm sm:text-base">Verifying face...</p></div>
                </div>
              )}
              {verificationStatus === 'success' && (
                <div className="absolute inset-0 bg-green-500 bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-center"><FiCheckCircle size={48} className="mx-auto mb-2" /><p className="text-lg font-bold">Verification Successful!</p></div>
                </div>
              )}
              {verificationStatus === 'failed' && (
                <div className="absolute inset-0 bg-red-500 bg-opacity-50 flex items-center justify-center">
                  <div className="text-white text-center"><FiXCircle size={48} className="mx-auto mb-2" /><p className="text-lg font-bold">Verification Failed</p></div>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-2 sm:gap-3">
              {!capturedImage && verificationStatus !== 'verifying' && (
                <button onClick={capturePhoto} disabled={!isVideoReady} className={`px-4 sm:px-6 py-2 rounded-xl transition text-sm sm:text-base flex-1 sm:flex-none ${isVideoReady ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-400 text-white cursor-not-allowed'}`}>📸 Capture Photo</button>
              )}
              {capturedImage && verificationStatus !== 'verifying' && verificationStatus !== 'success' && (
                <>
                  <button onClick={() => { setCapturedImage(null); setVerificationStatus(null); }} className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition text-sm sm:text-base flex-1 sm:flex-none">🔄 Retake</button>
                  <button onClick={() => verifyFace(capturedImage)} className="px-4 sm:px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-sm sm:text-base flex-1 sm:flex-none">✅ Verify Face</button>
                </>
              )}
              {verificationStatus === 'verifying' && (
                <button disabled className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-xl text-sm sm:text-base flex-1 sm:flex-none flex items-center justify-center gap-2"><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>Verifying...</button>
              )}
              {verificationStatus === 'success' && (
                <button onClick={() => { setShowCamera(false); setCapturedImage(null); setCaptureMode(null); setVerificationStatus(null); stopCamera(); }} className="px-4 sm:px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-sm sm:text-base flex-1 sm:flex-none">Done ✅</button>
              )}
              {verificationStatus === 'failed' && (
                <button onClick={() => { setCapturedImage(null); setVerificationStatus(null); }} className="px-4 sm:px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition text-sm sm:text-base flex-1 sm:flex-none">Try Again</button>
              )}
            </div>
            {!capturedImage && verificationStatus !== 'verifying' && (
              <p className="text-center text-xs text-gray-500 mt-3">{!isVideoReady ? '⏳ Please wait for camera to load...' : 'Position your face in the center and ensure good lighting'}</p>
            )}
            {capturedImage && verificationStatus !== 'verifying' && verificationStatus !== 'success' && (
              <p className="text-center text-xs text-green-600 mt-3">✅ Photo captured! Click "Verify Face" to continue</p>
            )}
          </div>
        </div>
      )}

      {/* Face Image Upload Modal for HR */}
      {showFaceUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">Manage Face Images</h3>
              <button onClick={() => { setShowFaceUpload(false); setSelectedEmployeeForFace(null); setFaceImages([]); setExistingFaceImages([]); }} className="text-gray-500 hover:text-gray-700 p-2"><FiX size={24} /></button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee</label>
              <select value={selectedEmployeeForFace || ''} onChange={(e) => { setSelectedEmployeeForFace(e.target.value); setFaceImages([]); }} className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 text-sm">
                <option value="">Select Employee</option>
                {globalEmployees.map(emp => {
                  const hasImages = existingFaceImages.some(img => imageBelongsToEmployee(img, emp.id));
                  return <option key={emp.id} value={emp.id}>{emp.name || emp.full_name} (ID: {emp.id}) {hasImages ? '✅' : '❌ No Images'}</option>;
                })}
              </select>
            </div>
            {selectedEmployeeForFace && (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2"><h4 className="text-sm font-medium text-gray-700">Existing Face Images</h4><span className="text-xs text-gray-500">{existingFaceImages.length} images</span></div>
                {loadingFaceImages ? (
                  <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
                ) : existingFaceImages.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {existingFaceImages.map((img, index) => (
                      <div key={img.id || index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                        {img.photo || img.image_url ? <img src={img.photo || img.image_url} alt={`Face ${index + 1}`} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-gray-200"><FiImage className="text-gray-400 text-2xl" /></div>}
                        <button onClick={() => deleteFaceImage(img.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition opacity-0 group-hover:opacity-100"><FiTrash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center"><p className="text-sm text-yellow-700">No face images uploaded for this employee</p></div>
                )}
              </div>
            )}
            {selectedEmployeeForFace && (
              <div className="mb-4 border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Face Images (Max 4 photos)</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input type="file" multiple accept="image/*" onChange={(e) => { const files = Array.from(e.target.files); const remaining = 4 - faceImages.length; if (files.length > remaining) { alert(`You can only upload ${remaining} more image(s).`); return; } setFaceImages(prev => [...prev, ...files]); }} className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 text-sm" />
                  <button onClick={() => { if (faceImages.length === 0) { alert('Please select at least 1 image to upload.'); return; } const existingPhotoNumbers = existingFaceImages.map(img => img.photo_number || 0); const allPhotoNumbers = [1, 2, 3, 4]; const availableSlots = allPhotoNumbers.filter(num => !existingPhotoNumbers.includes(num)); if (availableSlots.length < faceImages.length) { alert(`Only ${availableSlots.length} slot(s) available. You selected ${faceImages.length} image(s).`); return; } uploadFaceImages(); }} disabled={faceImages.length === 0 || uploadingFace || !selectedEmployeeForFace || existingFaceImages.length >= 4} className={`px-4 py-2 rounded-xl transition text-sm ${faceImages.length > 0 && selectedEmployeeForFace && !uploadingFace && existingFaceImages.length < 4 ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>{uploadingFace ? 'Uploading...' : `Upload ${faceImages.length} Image(s)`}</button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Upload up to 4 face images for verification</p>
                {faceImages.length > 0 && faceImages.length < 4 && <p className="text-xs text-blue-600 mt-1">You can add {4 - faceImages.length} more image(s)</p>}
                {faceImages.length === 4 && <p className="text-xs text-green-600 mt-1">Maximum images selected. Ready to upload!</p>}
              </div>
            )}
            {selectedEmployeeForFace && existingFaceImages.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div>
                    <p className="text-sm font-medium text-yellow-800">{existingFaceImages.filter(img => img.is_active).length} / 4 photos active</p>
                    {existingFaceImages.filter(img => img.is_active).length < 4 && <p className="text-xs text-yellow-600">Need {4 - existingFaceImages.filter(img => img.is_active).length} more active photo(s) for verification</p>}
                    {existingFaceImages.filter(img => img.is_active).length === 4 && <p className="text-xs text-green-600">All 4 photos active. Verification ready!</p>}
                  </div>
                  {existingFaceImages.filter(img => img.is_active).length < 4 && (
                    <button onClick={async () => { if (!selectedEmployeeForFace) return; try { const result = await attendanceService.activateAllFacePhotos(selectedEmployeeForFace); alert(`${result.activatedCount} photo(s) activated successfully!`); await loadExistingFaceImages(selectedEmployeeForFace); } catch (error) { alert('Failed to activate photos: ' + error.message); } }} className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm whitespace-nowrap">Activate All</button>
                  )}
                </div>
              </div>
            )}
            {faceImages.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">New Images to Upload ({faceImages.length}/4)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {faceImages.map((image, index) => (
                    <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      <img src={URL.createObjectURL(image)} alt={`New ${index + 1}`} className="w-full h-full object-cover" />
                      <button onClick={() => { const newImages = [...faceImages]; newImages.splice(index, 1); setFaceImages(newImages); }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><FiX size={14} /></button>
                      <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-2 py-0.5 rounded">#{index + 1}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowFaceUpload(false); setSelectedEmployeeForFace(null); setFaceImages([]); setExistingFaceImages([]); }} className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition text-sm">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Detail Modal */}
      {showEmployeeDetail && selectedEmployeeDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center"><FiUser className="text-indigo-600 text-xl" /></div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{selectedEmployeeDetail.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-500">Employee ID: {selectedEmployeeDetail.id || selectedEmployeeDetail.employeeId}</p>
                  {selectedEmployeeDetail.department && <p className="text-xs text-gray-400">{selectedEmployeeDetail.department}</p>}
                </div>
              </div>
              <button onClick={() => { setShowEmployeeDetail(false); setSelectedEmployeeDetail(null); setEmployeeStats(null); setEmployeeAttendanceRecords([]); }} className="text-gray-500 hover:text-gray-700 p-2"><FiX size={24} /></button>
            </div>

            {loadingEmployeeStats ? (
              <div className="text-center py-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div><p className="text-gray-500">Loading attendance data...</p></div>
            ) : employeeStats ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="bg-green-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Present</p><p className="text-xl font-bold text-green-600">{employeeStats.total_days_present || 0}</p></div>
                  <div className="bg-red-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Absent</p><p className="text-xl font-bold text-red-600">{employeeStats.total_days_absent || 0}</p></div>
                  <div className="bg-purple-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Late</p><p className="text-xl font-bold text-purple-600">{employeeStats.total_days_late || 0}</p></div>
                  <div className="bg-yellow-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Leave</p><p className="text-xl font-bold text-yellow-600">{employeeStats.total_days_leave || 0}</p></div>
                  <div className="bg-blue-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Working Days</p><p className="text-xl font-bold text-blue-600">{employeeStats.total_working_days || 0}</p></div>
                  <div className="bg-indigo-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Attendance %</p><p className="text-xl font-bold text-indigo-600">{parseFloat(employeeStats.attendance_percentage || 0).toFixed(1)}%</p></div>
                  <div className="bg-orange-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Total Hours</p><p className="text-xl font-bold text-orange-600">{parseFloat(employeeStats.total_hours_worked || 0).toFixed(1)}h</p></div>
                  <div className="bg-teal-50 rounded-xl p-3 text-center"><p className="text-xs text-gray-500">Overtime</p><p className="text-xl font-bold text-teal-600">{parseFloat(employeeStats.total_overtime_hours || 0).toFixed(1)}h</p></div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-700 mb-3">Detailed Summary</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Average Hours/Day</span><span className="font-medium">{parseFloat(employeeStats.average_hours_per_day || 0).toFixed(1)}h</span></div>
                    <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Late Minutes</span><span className="font-medium text-red-500">{parseFloat(employeeStats.total_late_minutes || 0).toFixed(0)} min</span></div>
                  </div>
                </div>

                {employeeAttendanceRecords && employeeAttendanceRecords.length > 0 && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Recent Attendance Records ({employeeAttendanceRecords.length})</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs sm:text-sm">
                        <thead className="bg-gray-50"><tr><th className="text-left px-3 py-2">Date</th><th className="text-center px-3 py-2">Check In</th><th className="text-center px-3 py-2">Check Out</th><th className="text-center px-3 py-2">Hours</th><th className="text-center px-3 py-2">Status</th></tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {employeeAttendanceRecords.slice(0, 10).map((record, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2">{record.date}</td>
                              <td className="px-3 py-2 text-center">{record.checkIn || '—'}</td>
                              <td className="px-3 py-2 text-center">{record.checkOut || '—'}</td>
                              <td className="px-3 py-2 text-center">{record.totalHours || '0'}h</td>
                              <td className="px-3 py-2 text-center">{getStatusBadge(record.status, false)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <FiInfo className="mx-auto text-gray-400 text-4xl mb-3" />
                <p className="text-gray-500">No attendance data available for this employee</p>
                <p className="text-xs text-gray-400 mt-1">Records will appear after the employee marks attendance</p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setShowEmployeeDetail(false); setSelectedEmployeeDetail(null); setEmployeeStats(null); setEmployeeAttendanceRecords([]); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition">Close</button>
              <button onClick={() => { if (selectedEmployeeDetail?.id) { fetchEmployeeStats(selectedEmployeeDetail.id); } }} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition flex items-center gap-2"><FiRefreshCw size={14} />Refresh</button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-5 mb-4 sm:mb-6">
        <div className="stat-card cursor-pointer hover:shadow-md transition p-3 sm:p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Present</p><p className="text-lg sm:text-2xl font-bold text-green-600 mt-1">{statistics.presentDays}</p><p className="text-xs text-green-600 mt-1 truncate">{statistics.attendancePercentage}%</p></div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0"><FiCheckCircle className="text-green-600 text-base sm:text-xl" /></div>
          </div>
        </div>
        <div className="stat-card cursor-pointer hover:shadow-md transition p-3 sm:p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Absent</p><p className="text-lg sm:text-2xl font-bold text-red-600 mt-1">{statistics.absent}</p></div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0"><FiXCircle className="text-red-600 text-base sm:text-xl" /></div>
          </div>
        </div>
        <div className="stat-card cursor-pointer hover:shadow-md transition p-3 sm:p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Leaves</p><p className="text-lg sm:text-2xl font-bold text-yellow-600 mt-1">{statistics.leaves}</p></div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0"><FiCalendar className="text-yellow-600 text-base sm:text-xl" /></div>
          </div>
        </div>
        <div className="stat-card cursor-pointer hover:shadow-md transition p-3 sm:p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Late</p><p className="text-lg sm:text-2xl font-bold text-purple-600 mt-1">{statistics.lateMarks}</p></div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0"><FiAlertCircle className="text-purple-600 text-base sm:text-xl" /></div>
          </div>
        </div>
        <div className="stat-card p-3 sm:p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Hours</p><p className="text-lg sm:text-2xl font-bold text-indigo-600 mt-1">{statistics.totalHoursWorked.toFixed(1)}h</p><p className="text-xs text-indigo-600 mt-1 truncate">Avg: {statistics.averageHoursPerDay.toFixed(1)}h</p></div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0"><FiBarChart2 className="text-indigo-600 text-base sm:text-xl" /></div>
          </div>
        </div>
        <div className="stat-card p-3 sm:p-4 bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="min-w-0"><p className="text-xs text-gray-500 font-medium">Overtime</p><p className="text-lg sm:text-2xl font-bold text-orange-600 mt-1">{statistics.totalOvertimeHours.toFixed(1)}h</p><p className="text-xs text-orange-600 mt-1 truncate">Late: {statistics.totalLateMinutes.toFixed(0)}min</p></div>
            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0"><FiClock className="text-orange-600 text-base sm:text-xl" /></div>
          </div>
        </div>
      </div>

      {/* Pie Chart + Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4"><FiPieChart className="text-indigo-600 text-lg sm:text-xl flex-shrink-0" /><h3 className="text-base sm:text-lg font-semibold text-gray-800">Attendance Summary</h3></div>
          <div className="h-56 sm:h-72 md:h-80">
            {pieChartData[0]?.name !== 'No Data' ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={30} outerRadius={60} paddingAngle={3} dataKey="value" label={({ name, percent }) => window.innerWidth > 640 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''} labelLine={window.innerWidth > 640}>
                    {pieChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} days`, name]} />
                  <Legend wrapperStyle={{ fontSize: window.innerWidth > 640 ? '12px' : '10px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full"><p className="text-gray-500 text-sm text-center">No attendance records yet.<br />Records will appear after marking attendance.</p></div>
            )}
          </div>
          <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 text-center truncate">{dateRange.start} to {dateRange.end}</div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Filters</h3>
          <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">{isAdminOrHR ? 'Filters affect statistics and pie chart' : 'Showing your attendance records only'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div><label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label><input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400" /></div>
            <div><label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label><input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {isAdminOrHR && (
              <div><label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Employee</label><select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"><option value="all">All Employees</option>{globalEmployees.map(emp => <option key={emp.id} value={emp.employee_id || emp.id}>{emp.name || emp.full_name}</option>)}</select></div>
            )}
            {isAdminOrHR && (
              <div><label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Department</label><select value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 bg-white"><option value="all">All Departments</option>{departments.map((dept, i) => <option key={i} value={dept}>{dept}</option>)}</select></div>
            )}
            {!isAdminOrHR && currentUser && (
              <div className="col-span-2">
                <div className="bg-blue-50 rounded-xl p-3 sm:p-4">
                  <div className="flex items-center gap-3"><FiUser className="text-blue-600 flex-shrink-0" size={18} /><div className="min-w-0"><p className="text-sm font-medium text-blue-900 truncate">{currentUser.first_name} {currentUser.last_name}</p><p className="text-xs text-blue-700">ID: {currentEmployeeId}</p></div></div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-3 sm:mt-4">
            <button onClick={handleRefresh} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-xs sm:text-sm flex items-center gap-1 sm:gap-2"><FiRefreshCw size={14} />Apply</button>
            {isAdminOrHR && (
              <>
                <button onClick={exportToCSV} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition text-xs sm:text-sm flex items-center gap-1 sm:gap-2"><FiDownload size={14} />Export</button>
                <button onClick={() => setShowFaceUpload(true)} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition text-xs sm:text-sm flex items-center gap-1 sm:gap-2"><FiCamera size={14} />Face Images</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Table Date Filter + Search */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
          <div className="flex items-center gap-2 flex-wrap">
            <FiFilter className="text-gray-400 flex-shrink-0" />
            <input type="date" value={selectedTableDate} onChange={(e) => setSelectedTableDate(e.target.value)} className="px-3 sm:px-4 py-2 sm:py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400 text-sm min-w-[140px] sm:min-w-[180px] flex-1 sm:flex-none" />
            <button onClick={() => setSelectedTableDate(getLocalDateString())} className="px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition whitespace-nowrap">Today</button>
          </div>
          {isAdminOrHR && (
            <div className="relative flex-1 w-full sm:max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input type="text" placeholder="Search by name or ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-400" />
            </div>
          )}
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
          <div><h3 className="text-base sm:text-lg font-semibold text-gray-800">Attendance Records</h3><p className="text-xs sm:text-sm text-gray-500">{isAdminOrHR ? `Records for ${selectedTableDate}${selectedTableDate === getLocalDateString() ? ' (Today)' : ''}` : `Your records for ${selectedTableDate}`}</p></div>
          <div className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">{tableFilteredData.filter(r => !r.isPlaceholder).length} / {tableFilteredData.length}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr><th className="text-left px-2 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Date</th><th className="text-left px-2 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Employee</th><th className="hidden sm:table-cell text-left px-2 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Dept</th><th className="text-center px-1 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Check In</th><th className="text-center px-1 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Check Out</th><th className="text-center px-1 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Total Hours</th><th className="text-center px-1 sm:px-4 py-2 sm:py-3 text-xs font-semibold text-gray-500 uppercase">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentItems.length > 0 ? (
                currentItems.map((record) => (
                  <tr key={record.id} className={`hover:bg-gray-50 transition cursor-pointer ${record.isPlaceholder ? 'bg-gray-50/50' : ''}`} onClick={() => { if (isAdminOrHR && !record.isPlaceholder) { handleEmployeeClick(record.employeeId, record.employeeName); } }}>
                    <td className="px-2 sm:px-4 py-2 sm:py-3"><span className="text-xs sm:text-sm text-gray-700">{record.date}</span></td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${record.isPlaceholder ? 'bg-gray-100' : 'bg-indigo-100'}`}><FiUser className={`${record.isPlaceholder ? 'text-gray-400' : 'text-indigo-600'} text-xs sm:text-sm`} /></div>
                        <div className="min-w-0"><p className="font-medium text-gray-800 text-xs sm:text-sm truncate max-w-[60px] sm:max-w-[120px] flex items-center gap-1">{record.employeeName}{isAdminOrHR && !record.isPlaceholder && <FiEye className="text-gray-400 text-[10px] sm:text-xs flex-shrink-0" />}</p><p className="text-[10px] sm:text-xs text-gray-500">{record.employeeId}</p></div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3"><span className="text-xs sm:text-sm text-gray-700">{record.department}</span></td>
                    <td className="px-1 sm:px-4 py-2 sm:py-3 text-center"><span className={`text-xs sm:text-sm ${record.isPlaceholder ? 'text-gray-400 italic' : 'text-gray-700'}`}>{record.checkIn || '—'}</span></td>
                    <td className="px-1 sm:px-4 py-2 sm:py-3 text-center"><span className={`text-xs sm:text-sm ${record.isPlaceholder ? 'text-gray-400 italic' : 'text-gray-700'}`}>{record.checkOut || '—'}</span></td>
                    <td className="px-1 sm:px-4 py-2 sm:py-3 text-center"><span className={`text-xs sm:text-sm font-medium ${record.isPlaceholder ? 'text-gray-400' : 'text-gray-800'}`}>{record.isPlaceholder ? '—' : `${record.totalHours}h`}</span></td>
                    <td className="px-1 sm:px-4 py-2 sm:py-3 text-center"><div className="flex justify-center">{getStatusBadge(record.status, record.isPlaceholder)}</div></td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={7} className="text-center py-8 sm:py-12"><FiCalendar className="mx-auto text-gray-400 text-3xl sm:text-5xl mb-3" /><p className="text-gray-500 text-sm sm:text-base">No attendance records for {selectedTableDate}</p><p className="text-xs sm:text-sm text-gray-400 mt-1">{selectedTableDate < getLocalDateString() ? 'No records were marked for this past date' : 'Select today to mark attendance'}</p></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {tableFilteredData.length > 0 && (
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, tableFilteredData.length)} of {tableFilteredData.length}</div>
            <div className="flex justify-center sm:justify-end gap-1 sm:gap-2 flex-wrap">
              <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-2 sm:px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"><FiChevronLeft size={14} /></button>
              {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                let p = totalPages <= 5 ? index + 1 : currentPage <= 3 ? index + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + index : currentPage - 2 + index;
                if (p >= 1 && p <= totalPages) {
                  return <button key={p} onClick={() => paginate(p)} className={`px-2 sm:px-3 py-1 rounded-lg transition text-sm ${currentPage === p ? 'bg-indigo-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>;
                }
                return null;
              })}
              <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"><FiChevronRight size={14} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Location Info Footer */}
      {location && !showCamera && !showLocationModal && (
        <div className="mt-3 sm:mt-4 bg-blue-50 rounded-xl p-2 sm:p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-blue-700"><FiMapPin size={14} /><span>📍 Location captured for attendance</span></div>
          <div className="text-[10px] sm:text-xs text-blue-600">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</div>
        </div>
      )}
    </div>
  );
};

export default Attendance;